import { useState, useEffect, useCallback, useRef } from 'react'
import { WaypointFoldersApi, WaypointFolderTree, CreateWaypointFolderDto, UpdateWaypointFolderDto } from '@/shared/api/waypointFolders'
import { WaypointResponse } from '@/shared/api/routes'

interface UseWaypointFoldersOptions {
  getAccessToken: () => Promise<string | null>
}

interface UseWaypointFoldersReturn {
  folders: WaypointFolderTree[]
  loading: boolean
  error: string | null
  expandedFolders: Set<string>
  toggleFolder: (folderId: string) => void
  createFolder: (data: CreateWaypointFolderDto) => Promise<WaypointFolderTree | null>
  updateFolder: (id: string, updates: UpdateWaypointFolderDto) => Promise<void>
  deleteFolder: (id: string) => Promise<void>
  moveWaypointToFolder: (waypointId: string, folderId: string | null) => Promise<void>
  refreshFolders: () => Promise<void>
}

const EXPANDED_FOLDERS_KEY = 'waypoint-folders-expanded'

export function useWaypointFolders({ getAccessToken }: UseWaypointFoldersOptions): UseWaypointFoldersReturn {
  const [folders, setFolders] = useState<WaypointFolderTree[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const apiRef = useRef<WaypointFoldersApi | null>(null)

  // Initialize API client
  useEffect(() => {
    apiRef.current = new WaypointFoldersApi(getAccessToken)
  }, [getAccessToken])

  // Load expanded folders from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(EXPANDED_FOLDERS_KEY)
      if (stored) {
        const expanded = new Set<string>(JSON.parse(stored))
        setExpandedFolders(expanded)
      }
    } catch (err) {
    }
  }, [])

  // Save expanded folders to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_FOLDERS_KEY, JSON.stringify(Array.from(expandedFolders)))
    } catch (err) {
    }
  }, [expandedFolders])

  // Fetch folders
  const refreshFolders = useCallback(async () => {
    if (!apiRef.current) return

    try {
      setLoading(true)
      setError(null)
      const fetchedFolders = await apiRef.current.getFolders()
      setFolders(fetchedFolders)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch folders'
      setError(errorMessage)

    } finally {
      setLoading(false)
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    refreshFolders()
  }, [refreshFolders])

  // Toggle folder expand/collapse
  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }, [])

  // Create folder
  const createFolder = useCallback(
    async (data: CreateWaypointFolderDto): Promise<WaypointFolderTree | null> => {
      if (!apiRef.current) return null

      try {
        const newFolder = await apiRef.current.createFolder(data)
        await refreshFolders()

        // Convert WaypointFolder to WaypointFolderTree
        return {
          ...newFolder,
          children: newFolder.children || []
        } as WaypointFolderTree
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create folder'

        throw err
      }
    },
    [refreshFolders]
  )

  // Update folder
  const updateFolder = useCallback(
    async (id: string, updates: UpdateWaypointFolderDto) => {
      if (!apiRef.current) return

      try {
        await apiRef.current.updateFolder(id, updates)
        await refreshFolders()

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update folder'

        throw err
      }
    },
    [refreshFolders]
  )

  // Delete folder
  const deleteFolder = useCallback(
    async (id: string) => {
      if (!apiRef.current) return

      try {
        await apiRef.current.deleteFolder(id)
        // Remove from expanded set
        setExpandedFolders((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
        await refreshFolders()

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete folder'

        throw err
      }
    },
    [refreshFolders]
  )

  // Move waypoint to folder
  const moveWaypointToFolder = useCallback(
    async (waypointId: string, folderId: string | null) => {
      if (!apiRef.current) return

      try {
        await apiRef.current.moveWaypoint(waypointId, folderId)
        // Don't refresh folders here - let the caller handle waypoint list refresh
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to move waypoint'

        throw err
      }
    },
    []
  )

  return {
    folders,
    loading,
    error,
    expandedFolders,
    toggleFolder,
    createFolder,
    updateFolder,
    deleteFolder,
    moveWaypointToFolder,
    refreshFolders,
  }
}

